import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';

import LandingPage from '@/pages/Landing';
import { AuthGate } from '@/components/auth/AuthGate';

// All non-landing routes are code-split. Landing stays eager so
// first-paint on the marketing entry point doesn't wait on a chunk fetch.
const NotFoundPage          = lazy(() => import('@/pages/NotFound'));
const OtpRequest            = lazy(() => import('@/pages/auth/OtpRequest').then(m => ({ default: m.OtpRequest })));
const OtpVerify             = lazy(() => import('@/pages/auth/OtpVerify').then(m => ({ default: m.OtpVerify })));
const ProfileSetup          = lazy(() => import('@/pages/auth/ProfileSetup').then(m => ({ default: m.ProfileSetup })));
const VendorApplication     = lazy(() => import('@/pages/auth/VendorApplication'));
const ForgotPassword        = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword         = lazy(() => import('@/pages/auth/ResetPassword'));
const VendorApprovals       = lazy(() => import('@/pages/admin/VendorApprovals'));
const PrivacyPolicy         = lazy(() => import('@/pages/legal/Privacy'));
const TermsOfService        = lazy(() => import('@/pages/legal/Terms'));
const StatusPage            = lazy(() => import('@/pages/Status'));

const CockpitLayout         = lazy(() => import('@/components/vendor/cockpit/CockpitLayout').then(m => ({ default: m.CockpitLayout })));
const VendorDashboard       = lazy(() => import('@/pages/vendor/Dashboard'));
const VendorLibrary         = lazy(() => import('@/pages/vendor/Library'));
const VendorCalendar        = lazy(() => import('@/pages/vendor/Calendar'));
const VendorBookings        = lazy(() => import('@/pages/vendor/Bookings'));
const VendorMoney           = lazy(() => import('@/pages/vendor/Money'));
const VendorProfile         = lazy(() => import('@/pages/vendor/Profile'));
const VendorSettings        = lazy(() => import('@/pages/vendor/Settings'));
const CockpitApprovals      = lazy(() => import('@/pages/vendor/Approvals'));

const AdminLayout           = lazy(() => import('@/components/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminDashboard        = lazy(() => import('@/pages/admin/AdminDashboard'));
const AuditLog              = lazy(() => import('@/pages/admin/AuditLog'));

const Discover              = lazy(() => import('@/pages/customer/Discover'));
const VendorProfilePage     = lazy(() => import('@/pages/customer/VendorProfilePage'));
const CustomerInbox         = lazy(() => import('@/pages/customer/Inbox'));
const CustomerPlan          = lazy(() => import('@/pages/customer/Plan'));
const PlanDetail            = lazy(() => import('@/pages/customer/PlanDetail'));
const CustomerSettings      = lazy(() => import('@/pages/customer/Settings'));
const CustomerSaved         = lazy(() => import('@/pages/customer/Saved'));
const CustomerMe            = lazy(() => import('@/pages/customer/Me'));
const ThreadDetail          = lazy(() => import('@/pages/shared/ThreadDetail'));

function RouteFallback() {
  // A thin orange progress strip at the very top instead of a full-screen
  // spinner. Browsers cache JS chunks aggressively, so subsequent
  // navigations finish in <50ms and this strip barely flashes — the
  // previous page content stays visible the whole time.
  return (
    <div
      className="fixed top-0 inset-x-0 h-0.5 bg-orange/80 z-50 animate-pulse"
      aria-hidden="true"
    />
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public — eager so the marketing page paints immediately */}
        <Route path="/" element={<LandingPage />} />

        {/* Legal pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* System status */}
        <Route path="/status" element={<StatusPage />} />

        {/* Auth — ProfileSetup checks account internally; no AuthGate
            needed (and AuthGate's loading state can stall on the
            fresh-after-verify session race). */}
        <Route path="/auth/login" element={<OtpRequest />} />
        <Route path="/auth/verify" element={<OtpVerify />} />
        <Route path="/auth/profile-setup" element={<ProfileSetup />} />
        <Route path="/auth/vendor-application" element={<VendorApplication />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />

        {/* Vendor cockpit — gated by AuthGate (super_admin bypasses
            the vendor_member check). */}
        <Route
          path="/vendor"
          element={
            <AuthGate requireRole="vendor_member">
              <CockpitLayout />
            </AuthGate>
          }
        >
          <Route index element={<VendorDashboard />} />
          <Route path="library" element={<VendorLibrary />} />
          <Route path="calendar" element={<VendorCalendar />} />
          <Route path="bookings" element={<VendorBookings />} />
          <Route path="money" element={<VendorMoney />} />
          <Route path="profile" element={<VendorProfile />} />
          <Route path="settings" element={<VendorSettings />} />
          {/* Embedded approvals queue — super_admin only, lives inside
              the cockpit chrome so it feels like another tab.
              The full admin section at /admin still hosts audit log etc. */}
          <Route path="approvals" element={<CockpitApprovals />} />
        </Route>

        {/* Customer — public */}
        <Route path="/discover" element={<Discover />} />
        <Route path="/v/:slug" element={<VendorProfilePage />} />

        {/* Customer inbox list. Detail uses the shared ThreadDetail page. */}
        <Route path="/account/inbox" element={<CustomerInbox />} />
        <Route path="/account/inbox/:id" element={<ThreadDetail />} />

        {/* Customer Plan dashboard */}
        <Route path="/account/plan" element={<CustomerPlan />} />
        <Route path="/account/plan/:id" element={<PlanDetail />} />

        {/* Customer settings */}
        <Route path="/account/settings" element={<CustomerSettings />} />

        {/* Customer saved-vendors list */}
        <Route path="/account/saved" element={<CustomerSaved />} />

        {/* Customer profile (Instagram-style "Me" tab) */}
        <Route path="/account/me" element={<CustomerMe />} />

        {/* Vendor side detail also uses the shared chat UI (perspective is
            derived from the signed-in user inside ThreadDetail). */}
        <Route path="/vendor/bookings/:id" element={<ThreadDetail />} />

        {/* Admin section — super_admin only, gated by AuthGate */}
        <Route
          path="/admin"
          element={
            <AuthGate requireRole="super_admin">
              <AdminLayout />
            </AuthGate>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="vendors" element={<VendorApprovals />} />
          <Route path="audit" element={<AuditLog />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
